import { useContext, useEffect, useState } from "react";

import { Preview } from "@creatomate/preview";
import { MillisecondsFieldValue } from "@culturehq/components/dist/components/form/MillisecondsField";

import { CreatomateContext } from "components/stories/video-studio/contexts/VideoStudioContext";
import { AUDIO_TYPE, VIDEO_TYPE } from "../utils/constants";

const useElementStartEnd = (preview: Preview) => {
  const { getActiveElement } = useContext(CreatomateContext);

  const [endAt, setEndAt] = useState(0);

  const activeElement = getActiveElement(preview);
  const currentTrack = activeElement?.track;
  const globalTime = activeElement?.globalTime || 0;
  const elementDuration = activeElement?.duration || 0;
  const startsInMilliSeconds = globalTime * 100;
  const newEndTime = (globalTime + endAt) * 100;
  const maxEndTime = (globalTime + elementDuration) * 100;

  const elementType = activeElement?.source?.type;
  const isMaxDurationType = elementType === VIDEO_TYPE || elementType === AUDIO_TYPE;
  const endsInMilliSeconds = isMaxDurationType ? Math.min(newEndTime, maxEndTime) : newEndTime;
  const activeElementSourceId = activeElement?.source?.id;
  const checkElementInstance = preview && activeElementSourceId;

  const trackElements = preview.getElements().filter(
    element => element.track === currentTrack).sort((a, b) => a.globalTime - b.globalTime);

  const currentIndex = trackElements.findIndex(te => te.source.id === activeElement?.source.id);

  useEffect(() => setEndAt(elementDuration), [elementDuration]);

  const applyDuration = async (duration: number) => {
    const checkMin = (globalTime + duration) >= globalTime;
    if (!checkMin) return;

    await preview.applyModifications({
      [`${activeElementSourceId}.duration`]: duration
    });
  };

  const handleOnStartsAtChange = async (value: MillisecondsFieldValue) => {
    if (!checkElementInstance || typeof value !== "number" || value < 0 || !activeElement) return;

    const endTime = activeElement.globalTime + activeElement.duration;
    const newValue = (value / 100);

    if (newValue > (endTime - 0.5)) return;

    if (trackElements.length > 0) {
      const prevElement = currentIndex === 0 ? undefined : trackElements[currentIndex - 1];

      if (prevElement) {
        const prevEndTime = Math.floor(prevElement.globalTime + prevElement.duration);
        if (newValue < prevEndTime) {
          return;
        }
      }
    }

    const newDuration = endTime - newValue;

    await preview.applyModifications({
      [`${activeElementSourceId}.time`]: newValue,
      [`${activeElementSourceId}.duration`]: newDuration
    });
  };

  const handleOnEndsAtChange = (value: MillisecondsFieldValue) => {
    if (!checkElementInstance || typeof value !== "number" || value < 0 || !activeElement) return;

    const newDuration = (value - startsInMilliSeconds) / 100;
    const newEndTime = activeElement.globalTime + newDuration;

    if (newDuration < 0.5) return;

    if (trackElements.length > 0) {
      const nextElement = currentIndex === trackElements.length - 1
        ? undefined : trackElements[currentIndex + 1];

      if (nextElement) {
        if (newEndTime > nextElement.globalTime) return;
      }
    }
    setEndAt(newDuration);
    applyDuration(newDuration);
  };

  return {
    startsInMilliSeconds,
    endsInMilliSeconds,
    handleOnStartsAtChange,
    handleOnEndsAtChange
  };
};

export default useElementStartEnd;
